import { FC, Fragment } from "preact/compat";
import { useEffect, useLayoutEffect, useRef } from "preact/hooks";
import { useComputed, useSignal } from "@preact/signals";
import { useApp } from "~/core";
import { WheelChoice, WheelProps } from "./interfaces";

const OPTIONS = {
    count: 20,
    sensitivity: 3.2,
    height: 300,
    easing: (x: number) => -(Math.pow(x - 1, 4) - 1),
};

const loopChoices = (choices: WheelChoice[], hCount: number) => {
    let result = choices.slice();

    while (result.length < hCount) {
        result = result.concat(choices);
    }

    return result;
};

const Wheel: FC<WheelProps> = ({ wheel, values, index, onChange }) => {
    const { mobile } = useApp();
    const options = OPTIONS;
    const itemHeight = (options.height * 3) / options.count;
    const itemAngle = 360 / options.count;
    const radius = itemHeight / Math.tan((itemAngle * Math.PI) / 180);
    const choices =
        "value" in wheel.choices ? wheel.choices.value : wheel.choices;
    const loopedChoices = loopChoices(choices, options.count / 2);
    const qCount = options.count / 4;

    const updateTag = useSignal(0);
    const updateListener = useRef(() => {});
    const valueSignal = useComputed(() => values.value[index]);
    const ringRef = useRef<HTMLUListElement>(null);
    const highlightRef = useRef<HTMLUListElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const ring = ringRef.current;
        const container = containerRef.current;
        const highlight = highlightRef.current;

        if (!ring || !container || !highlight) {
            return;
        }

        const touchData = {
            y: [] as [number, number][],
            scroll: 0,
            startY: 0,
        };
        let items = Array.from(
            ring.querySelectorAll<HTMLDivElement>("[data-index]"),
        );
        let isTouchStarted = false;
        let moving = false;
        let scroll = 0;
        let requestId = 0;
        let lastValue = "";
        let choicesLength = choices.length;
        let looped = loopChoices(choices, options.count / 2);
        let allowImpact = false;
        let programmatic = false;
        let lastIndex = -1;

        void moving; // @TODO: remove or use

        const stop = () => {
            window.cancelAnimationFrame(requestId);
            moving = false;
        };

        const normalizeScroll = (s: number) => {
            let ns = s;

            while (ns < 0) {
                ns += choicesLength;
            }

            ns = ns % choicesLength;

            return ns;
        };

        const moveTo = (s: number) => {
            s = normalizeScroll(s);

            ring.style.transform = `translate3d(0, 0, -${radius}px) rotateX(${
                itemAngle * s
            }deg)`;
            highlight.style.transform = `translate3d(0, ${
                -s * itemHeight
            }px, 0)`;
            items.forEach((i) => {
                const idx = Number(i.dataset.index ?? "0");

                if (Math.abs(idx - s) > qCount) {
                    i.style.visibility = "hidden";
                } else {
                    i.style.visibility = "visible";
                }
            });

            const currentIndex = Math.round(s);

            if (allowImpact && !programmatic && currentIndex !== lastIndex) {
                lastIndex = currentIndex;
                mobile.generateImpact("light");
            }

            return s;
        };

        const selectByScroll = (s: number) => {
            s = Math.min(normalizeScroll(s) | 0, looped.length - 1);

            moveTo(s);
            scroll = s;
            lastValue = looped[s].value;
            onChange(looped[s].value);
        };

        const select = (value: string) => {
            const choiceIdx = looped.findIndex((c) => c.value === value);

            if (choiceIdx === -1 || choiceIdx === scroll) {
                return false;
            }

            stop();

            const from = scroll;
            const to = choiceIdx;
            const t = Math.sqrt(
                Math.abs((to - from) / (options.sensitivity * 15)),
            );

            programmatic = true;

            animateToScroll(from, to, t).then(() => {
                programmatic = false;
            });
            window.setTimeout(() => selectByScroll(choiceIdx), 0);

            return true;
        };

        const animateToScroll = (from: number, to: number, t: number) => {
            if (from === to || t === 0) {
                moveTo(to);
                return Promise.resolve();
            }

            const start = new Date().getTime() / 1000;
            const tsl = to - from;
            let pass = 0;

            return new Promise<void>((resolve) => {
                moving = true;
                allowImpact = true;

                const tick = () => {
                    pass = new Date().getTime() / 1000 - start;

                    if (pass < t) {
                        scroll = moveTo(from + options.easing(pass / t) * tsl);
                        requestId = requestAnimationFrame(tick);
                    } else {
                        resolve();
                        stop();
                        allowImpact = false;
                        scroll = moveTo(from + tsl);
                    }
                };

                tick();
            });
        };

        const animateMove = async (initV: number) => {
            const ia = options.sensitivity * 10;
            const initScroll = scroll;
            const a = initV > 0 ? -ia : ia;
            const t = Math.max(Math.abs(initV / a), 0.1);
            const tsl = initV * t + (a * t * t) / 2;
            const finalScroll = Math.round(initScroll + tsl);

            await animateToScroll(initScroll, finalScroll, t);

            selectByScroll(scroll);
        };

        const handleTouchStart = (e: TouchEvent) => {
            const eventY = e.touches[0].clientY;

            isTouchStarted = true;
            allowImpact = true;
            programmatic = false;
            lastIndex = Math.round(scroll);

            touchData.startY = eventY;
            touchData.y = [[eventY, Date.now()]];
            touchData.scroll = scroll;

            stop();
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (!isTouchStarted) return;

            e.preventDefault();

            const eventY = e.touches[0].clientY;
            const scrollAdd = (touchData.startY - eventY) / itemHeight;
            let moveToScroll = scroll + scrollAdd;

            touchData.y.push([eventY, Date.now()]);

            if (touchData.y.length > 5) {
                touchData.y.shift();
            }

            moveToScroll = normalizeScroll(moveToScroll);

            touchData.scroll = moveTo(moveToScroll);
        };

        const handleTouchEnd = () => {
            if (!isTouchStarted) return;

            let v = 0;

            if (touchData.y.length !== 1) {
                const startTime = touchData.y[touchData.y.length - 2][1];
                const endTime = touchData.y[touchData.y.length - 1][1];
                const startY = touchData.y[touchData.y.length - 2][0];
                const endY = touchData.y[touchData.y.length - 1][0];

                v =
                    (((startY - endY) / itemHeight) * 1000) /
                    (endTime - startTime);
                const sign = v > 0 ? 1 : -1;

                v = Math.abs(v) > 30 ? 30 * sign : v;
            }

            scroll = touchData.scroll;
            animateMove(v);
            isTouchStarted = false;
        };

        container.addEventListener("touchstart", handleTouchStart);
        window.addEventListener("touchmove", handleTouchMove, {
            passive: false,
        });
        window.addEventListener("touchend", handleTouchEnd);
        updateListener.current = () => {
            if ("value" in wheel.choices) {
                const newValue = wheel.choices.peek();

                choicesLength = newValue.length;
                looped = loopChoices(newValue, options.count / 2);
                items = Array.from(
                    ring.querySelectorAll<HTMLDivElement>("[data-index]"),
                );

                if (!select(valueSignal.value)) {
                    moveTo(scroll);
                }
            }
        };

        const choicesUnsubscribe =
            "value" in wheel.choices
                ? wheel.choices.subscribe(() => updateTag.value++)
                : null;

        const valueUnsubscribe = valueSignal.subscribe((v) => {
            if (lastValue !== v) {
                select(v);
            }
        });

        moveTo(0);

        return () => {
            container.removeEventListener("touchstart", handleTouchStart);
            window.removeEventListener("touchmove", handleTouchMove);
            window.removeEventListener("touchend", handleTouchEnd);
            valueUnsubscribe();
            choicesUnsubscribe?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options.count]);

    useLayoutEffect(() => updateListener.current(), [updateTag.value]);

    return (
        <div
            ref={containerRef}
            style={{
                perspective: "2000px",
                height: options.height,
            }}
            className="relative w-full text-lg"
        >
            <div className="h-full relative wheel-input-ring-wrap">
                <ul
                    ref={ringRef}
                    className="absolute block top-1/2 left-0 w-full h-0 wheel-input-ring text-gray-500 font-bold"
                >
                    {loopedChoices.map((choice, idx) => (
                        <li
                            key={idx}
                            style={{
                                top: itemHeight * -0.5,
                                height: itemHeight,
                                lineHeight: `${itemHeight}px`,
                                transform: `rotateX(${
                                    -itemAngle * idx
                                }deg) translate3d(0, 0, ${radius}px)`,
                            }}
                            className="absolute block w-full"
                            data-index={idx}
                        >
                            {choice.label}
                        </li>
                    ))}
                    {Array.from({ length: qCount }).map((_, idx) => (
                        <Fragment key={idx}>
                            <li
                                style={{
                                    top: itemHeight * -0.5,
                                    height: itemHeight,
                                    lineHeight: `${itemHeight}px`,
                                    transform: `rotateX(${
                                        itemAngle * (idx + 1)
                                    }deg) translate3d(0, 0, ${radius}px)`,
                                }}
                                className="absolute block w-full"
                                data-index={-idx - 1}
                            >
                                {
                                    loopedChoices[
                                        loopedChoices.length - idx - 1
                                    ].label
                                }
                            </li>
                            <li
                                style={{
                                    top: itemHeight * -0.5,
                                    height: itemHeight,
                                    lineHeight: `${itemHeight}px`,
                                    transform: `rotateX(${
                                        -itemAngle *
                                        (idx + loopedChoices.length)
                                    }deg) translate3d(0, 0, ${radius}px)`,
                                }}
                                className="absolute block w-full"
                                data-index={idx + loopedChoices.length}
                            >
                                {loopedChoices[idx].label}
                            </li>
                        </Fragment>
                    ))}
                </ul>
                <div
                    style={{
                        height: itemHeight,
                        lineHeight: `${itemHeight}px`,
                    }}
                    className="absolute top-1/2 -translate-y-1/2 w-full bg-white border-y border-gray-300 overflow-hidden font-bold"
                >
                    <ul
                        ref={highlightRef}
                        style={{
                            top: -itemHeight,
                        }}
                        className="w-full absolute"
                    >
                        <li
                            style={{
                                height: itemHeight,
                                lineHeight: `${itemHeight}px`,
                            }}
                        >
                            {loopedChoices[loopedChoices.length - 1].label}
                        </li>
                        {loopedChoices.map((choice, idx) => (
                            <li
                                key={idx}
                                style={{
                                    height: itemHeight,
                                    lineHeight: `${itemHeight}px`,
                                }}
                            >
                                {choice.label}
                            </li>
                        ))}
                        <li
                            style={{
                                height: itemHeight,
                                lineHeight: `${itemHeight}px`,
                            }}
                        >
                            {loopedChoices[0].label}
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default Wheel;
